import { Client } from 'src/admin/users/types';
import { CreateFamilyPayload, FamilyMemberWithRelation } from '../hooks/useCreateFamily';
import { Relation } from '../hooks/useFamily';

const INVERSE_RELATION: Record<Relation, Relation> = {
  Spouse: 'Spouse',
  Partner: 'Partner',
  Child: 'Parent',
  Parent: 'Child',
  Sibling: 'Sibling',
  Other: 'Other',
};

export function getInverseRelation(relation: Relation): Relation {
  return INVERSE_RELATION[relation];
}

export type V2MemberRelationship = Record<string, Relation | null>;

export type InProgressFamilyV2 = {
  selectedMembers: Client[];
  relationships: Record<string, V2MemberRelationship>;
  name: string;
};

export type V2MemberRoles = Record<string, string | null>;

type InProgressFamilyRolesV2 = {
  selectedMembers: Client[];
  roles: V2MemberRoles;
  name: string;
};

export function isMemberSelected(client: Client, selectedMembers: Client[]): boolean {
  return selectedMembers.some(m => m.id === client.id);
}

export function starModelPayloadFromInProgressFamilyV2(
  family: InProgressFamilyV2,
  advisorUserId: string,
): CreateFamilyPayload {
  const [primary, ...secondaries] = family.selectedMembers;

  const relationships = Object.entries(family.relationships).flatMap(([fromId, toMap]) =>
    Object.entries(toMap)
      .filter(([, relation]) => relation !== null)
      .map(([toId]) => ({
        fromClientId: fromId,
        toClientId: toId,
        // Backend peer-edge label convention: A→B is labelled with B's relationship TO A
        // (the target's perspective), matching how _assert_star_is_subgraph_of_peer checks edges.
        relationshipType: family.relationships[toId]?.[fromId] as Relation,
      })),
  );

  // When we send the peer graph, the backend derives the legacy star shape from
  // it and rejects a family where the primary has no forward edge to a member.
  // Users can relate members to each other while leaving the primary unrelated,
  // so once the graph is non-empty, backfill any missing primary↔member edges
  // with the same 'Other' default applied to secondaryFamilyMembers (mirroring
  // the V1 backend path) — keeping it star-derivable while preserving the
  // inter-member edges the user set. When no relationships are set at all we
  // leave the graph empty and omit `members`, so the backend derives the star
  // shape from secondaryFamilyMembers instead.
  if (relationships.length > 0) {
    const hasEdge = (fromId: string, toId: string) =>
      relationships.some(r => r.fromClientId === fromId && r.toClientId === toId);
    for (const member of secondaries) {
      const memberToPrimary =
        (family.relationships[member.id]?.[primary.id] as Relation) ?? 'Other';
      const primaryToMember =
        (family.relationships[primary.id]?.[member.id] as Relation) ??
        getInverseRelation(memberToPrimary);
      if (!hasEdge(primary.id, member.id)) {
        relationships.push({
          fromClientId: primary.id,
          toClientId: member.id,
          relationshipType: memberToPrimary,
        });
      }
      if (!hasEdge(member.id, primary.id)) {
        relationships.push({
          fromClientId: member.id,
          toClientId: primary.id,
          relationshipType: primaryToMember,
        });
      }
    }
  }

  return {
    name: family.name,
    advisorUserId,
    mainFamilyMember: { clientId: primary.id },
    secondaryFamilyMembers: secondaries.map(
      member =>
        ({
          clientId: member.id,
          relationshipToMainFamilyMember: family.relationships[member.id]?.[primary.id] ?? 'Other',
        }) as FamilyMemberWithRelation,
    ),
    ...(relationships.length > 0 && {
      members: family.selectedMembers.map(m => ({ clientId: m.id })),
    }),
    relationships,
  };
}

export function roleModelPayloadFromInProgressFamilyV2(
  family: InProgressFamilyRolesV2,
  advisorUserId: string,
): CreateFamilyPayload {
  return {
    name: family.name,
    advisorUserId,
    members: family.selectedMembers.map(m => ({
      clientId: m.id,
      familyRoleId: family.roles[m.id] ?? null,
    })),
  };
}
